// ── Reading an upgrade quote ────────────────────────────────────────────────
//
// The confirmation modal used to print Stripe's invoice preview verbatim. Its
// own comment said so — "Itemised lines, exactly as the provider returned
// them" — and for one upgrade that is survivable:
//
//     Unused time on Grow after 18 Aug 2026        -$12.00
//     Remaining time on Optimize after 18 Aug 2026  $36.13
//
// Change plan twice in a period and Stripe sends FOUR such lines, because it
// bills every change as a pair. The customer was handed the arithmetic:
//
//     Unused time on ImproveSEO Subscription after 24 Aug 2026    -$22.64
//     Remaining time on ImproveSEO Subscription after 24 Aug 2026  $44.83
//     Unused time on ImproveSEO Subscription after 21 Aug 2026     -$9.34
//     Remaining time on ImproveSEO Subscription after 21 Aug 2026  $24.58
//
// Nothing there says two of those lines came from a decision made three days
// ago, or that "ImproveSEO Subscription" was Grow at the time.
//
// The server now groups the lines by the change that produced them and names
// the plans from our own records. This module turns that into rows.

import { formatMoney } from './money';
import { getPlanDisplayName } from './planUtils';

/** 'Sep 19, 2026'. UTC, so a date-only value cannot slip a day. */
export function formatQuoteDate(value) {
  if (value == null) return null;
  const d = value instanceof Date
    ? value
    // Unix SECONDS from Stripe, or an ISO string from our own database.
    : (typeof value === 'number' ? new Date(value * 1000) : new Date(value));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

/**
 * The name the CUSTOMER knows a plan by.
 *
 * The database calls them Starter and Pro; every screen calls them Optimize and
 * Scale. The server sends the raw names, so the mapping has to happen here —
 * without it this dialog would be the one place in the product that names a
 * plan differently from the card the customer just clicked.
 *
 * The trailing " Plan" is then stripped because the surrounding copy already
 * says the word: "the Optimize Plan plan" otherwise.
 */
const planLabel = (name) => {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  const display = getPlanDisplayName({ name: trimmed }) || trimmed;
  return String(display).replace(/\s+plan$/i, '');
};

/** "the 26 days left in this period", or the vaguer phrase when unknown. */
export function periodRemainingPhrase(days) {
  if (typeof days !== 'number' || !Number.isFinite(days) || days < 0) {
    return 'the rest of this period';
  }
  return `the ${days.toLocaleString()} day${days === 1 ? '' : 's'} left in this period`;
}

/**
 * The rows behind "See how this is calculated".
 *
 * ── WHY THE TWO CASES DIFFER ────────────────────────────────────────────────
 * With ONE change there is room to show the pair itemised — the charge for the
 * new plan and the credit for what is unused — because that is the whole bill
 * and the two halves explain each other.
 *
 * With SEVERAL, itemising every pair is the four-line wall this replaces. So
 * the current change collapses to its net, and each earlier change becomes one
 * line naming what it was. The reader sees one row per decision they made.
 *
 * @param changes  the server's grouped lines, current first
 * @param opts     { toPlan, fromPlan, daysRemaining, currency }
 */
export function buildQuoteBreakdown(changes, opts = {}) {
  const list = Array.isArray(changes) ? changes : [];
  if (list.length === 0) return [];

  const currency = opts.currency || 'usd';
  const to = planLabel(opts.toPlan);
  const from = planLabel(opts.fromPlan);
  const current = list.find((c) => c.is_current) ?? list[0];
  const earlier = list.filter((c) => c !== current);

  const rows = [];

  if (earlier.length === 0) {
    // One change: show the pair. The charge names the plan being bought and
    // the window it buys; the credit names the plan being left.
    rows.push({
      key: 'current-charge',
      label: to
        ? `${to} plan for ${periodRemainingPhrase(opts.daysRemaining)}`
        : `Your new plan for ${periodRemainingPhrase(opts.daysRemaining)}`,
      amount: formatMoney(current.charge_cents, currency),
      amountCents: current.charge_cents,
    });
    if (current.credit_cents !== 0) {
      rows.push({
        key: 'current-credit',
        label: from ? `Unused time on the ${from} plan` : 'Unused time on your previous plan',
        amount: formatMoney(current.credit_cents, currency),
        amountCents: current.credit_cents,
      });
    }
  } else {
    // Several: this change netted, so the earlier ones have room to be named.
    rows.push({
      key: 'current-net',
      label: to && from
        ? `${to} plan for the rest of this period, less unused time on the ${from} plan`
        : 'This change, less unused time on your previous plan',
      amount: formatMoney(current.amount_cents, currency),
      amountCents: current.amount_cents,
    });

    earlier.forEach((change, i) => {
      const when = formatQuoteDate(change.at);
      const a = planLabel(change.from_plan);
      const b = planLabel(change.to_plan);
      // Named where the line's price matched a plan; otherwise the date and the
      // amount still identify it, which beats inventing plan names.
      const what = a && b ? `your change from ${a} to ${b}` : 'an earlier change';
      // "Unpaid" because that is the only way an earlier change reaches this
      // quote: the server charges each upgrade when it is made, so its lines
      // are still on the subscription only if that charge did not go through.
      // Without the word the customer reads it as being billed twice.
      rows.push({
        key: `earlier-${change.at ?? 'na'}-${i}`,
        label: when ? `Unpaid adjustment for ${what} on ${when}` : `Unpaid adjustment for ${what}`,
        amount: formatMoney(change.amount_cents, currency),
        amountCents: change.amount_cents,
      });
    });
  }

  return rows;
}

/**
 * What the amounts above were prorated against.
 *
 * ── WHY THIS LINE EXISTS ────────────────────────────────────────────────────
 * QA read "Scale plan for the 30 days left in this period — $49.42" beside
 * "Then $49.50 per month" and asked why the calculation had odd decimals. Stripe
 * was prorating to the SECOND. The server now prices every upgrade from the
 * start of the current billing day, so the amounts are whole-day figures and
 * day 0 of a 30-day period is exactly $49.50.
 *
 * Months that are not 30 days long still produce cents (30 of 31 days of
 * $49.50 is $47.90), so the basis is still worth saying. Every figure in this
 * dialog remains Stripe's, because the number shown has to be the number
 * charged.
 *
 * Returns null without a period end, so the caller renders nothing rather than
 * a sentence trailing off.
 */
export function prorationBasisNote(periodEnd) {
  const when = formatQuoteDate(periodEnd);
  if (!when) return null;
  return `Prorated by the whole days left in this billing period, which ends ${when}.`;
}

/**
 * "+570 credits, added immediately — new balance will be 1,240"
 *
 * ── WHY "WILL BE", AND WHY THE BALANCE IS A SUM ─────────────────────────────
 * This is shown BEFORE the customer confirms, so every verb is future tense:
 * nothing has been granted at the point they read it. The earlier wording
 * ("added now — new balance 1,240") described the grant as already done.
 *
 * `balance_after` is the server's own `current balance + granted_now` — the
 * upgrade ADDS to what the customer already holds rather than replacing it, and
 * this line is where they see that. 1,240 is 670 they had plus 570 arriving,
 * not the new plan's monthly allowance. The renewal allowance is a separate
 * line, deliberately, because they are different numbers and were being
 * confused for each other.
 *
 * Returns null when the change grants nothing, so the caller renders no credit
 * section at all rather than a row of zeroes.
 */
export function creditHeadline(credits) {
  const granted = Number(credits?.granted_now ?? 0);
  if (!granted) return null;
  const balance = credits?.balance_after;
  const n = (v) => Number(v).toLocaleString();
  return typeof balance === 'number' && Number.isFinite(balance)
    ? `+${n(granted)} credits, added immediately — new balance will be ${n(balance)}`
    : `+${n(granted)} credits, added immediately`;
}

/**
 * "That's the increase from the Optimize to the Scale plan for the 26 days
 * left in this period."
 *
 * Only when the grant really was scaled down. Saying a full grant was prorated
 * would explain away credits the customer did in fact receive in full.
 */
export function creditExplanation(credits, { fromPlan, toPlan, daysRemaining } = {}) {
  if (!credits?.prorated) return null;
  const from = planLabel(fromPlan);
  const to = planLabel(toPlan);
  if (!from || !to) return null;
  return `That's the increase from the ${from} to the ${to} plan for ${periodRemainingPhrase(daysRemaining)}.`;
}
